export interface ButtonProps {
  text: string;
  icon?: string;
  onClick?: string;
  id?: string;
  className?: string; // Additional utility classes
}

export function BtnPrimary({ text, icon, onClick, id, className = '' }: ButtonProps): string {
  const iconHtml = icon ? `<span class="icon">${icon}</span>` : '';
  const idAttr = id ? `id="${id}"` : '';
  const actionAttr = onClick ? `data-action="${onClick}"` : '';

  return `
    <button
      ${idAttr}
      ${actionAttr}
      class="flex items-center justify-center gap-[8px] w-full py-[17px] px-[24px] bg-grad-primary rounded-pill text-white font-body text-[17px] font-extrabold tracking-[0.2px] cursor-pointer shadow-glow-primary transition-all duration-[180ms] ease-out relative overflow-hidden active:scale-[0.97] active:shadow-[0_0_16px_rgba(255,45,107,0.4)] before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/15 before:to-transparent before:opacity-0 before:transition-opacity before:duration-[180ms] active:before:opacity-100 ${className}"
    >
      ${iconHtml}
      <span>${text}</span>
    </button>
  `.trim();
}

export function BtnSecondary({ text, icon, onClick, id, className = '' }: ButtonProps): string {
  const iconHtml = icon ? `<span class="icon">${icon}</span>` : '';
  const idAttr = id ? `id="${id}"` : '';
  const actionAttr = onClick ? `data-action="${onClick}"` : '';

  return `
    <button
      ${idAttr}
      ${actionAttr}
      class="flex items-center justify-center gap-[8px] w-full py-[15px] px-[24px] bg-glass border border-b-glass rounded-pill text-t-primary font-body text-[16px] font-bold cursor-pointer backdrop-blur-[12px] transition-all duration-[180ms] ease-out active:scale-[0.97] active:bg-white/10 ${className}"
    >
      ${iconHtml}
      <span>${text}</span>
    </button>
  `.trim();
}

export interface CoinChipProps {
  amount: number | string;
  id?: string;
  onClick?: string;
  className?: string;
}

export function CoinChip({ amount, id, onClick, className = '' }: CoinChipProps): string {
  const idAttr = id ? `id="${id}"` : '';
  const actionAttr = onClick ? `data-action="${onClick}"` : '';

  return `
    <div
      ${idAttr}
      ${actionAttr}
      class="flex items-center gap-[6px] bg-neon-gold/[0.12] border border-neon-gold/[0.25] rounded-pill py-[6px] px-[12px] cursor-pointer ${className}"
    >
      <span class="text-[16px]">🪙</span>
      <span class="font-extrabold text-[14px] text-neon-gold" data-coin-amount>${amount}</span>
    </div>
  `.trim();
}

export type BadgeVariant = 'premium' | 'free' | 'coin' | 'locked' | 'invite';

export interface BadgeProps {
  text: string;
  variant: BadgeVariant;
  icon?: string;
  className?: string;
}

export function Badge({ text, variant, icon, className = '' }: BadgeProps): string {
  const baseClasses = "inline-flex items-center gap-[4px] py-[3px] px-[9px] rounded-pill text-[11px] font-extrabold tracking-[0.5px]";
  
  let variantClasses = "";
  switch (variant) {
    case 'premium':
      variantClasses = "bg-grad-premium text-[#1a0a00]";
      break;
    case 'free':
      variantClasses = "bg-neon-green/15 text-neon-green border border-neon-green/30";
      break;
    case 'coin':
      variantClasses = "bg-neon-gold/15 text-neon-gold border border-neon-gold/30";
      break;
    case 'locked':
      variantClasses = "bg-white/[0.08] text-t-muted border border-white/10";
      break;
    case 'invite':
      variantClasses = "bg-grad-blue text-white border-none shadow-glow-blue";
      break;
  }

  const iconHtml = icon ? `<span>${icon}</span>` : '';

  return `
    <div class="${baseClasses} ${variantClasses} ${className}">
      ${iconHtml}
      <span>${text}</span>
    </div>
  `.trim();
}

export interface GlassCardProps {
  children: string;
  className?: string;
  id?: string;
}

export function GlassCard({ children, className = '', id }: GlassCardProps): string {
  const idAttr = id ? `id="${id}"` : '';

  return `
    <div 
      ${idAttr}
      class="bg-glass border border-b-glass rounded-lg backdrop-blur-[16px] p-md relative overflow-hidden ${className}"
    >
      ${children}
    </div>
  `.trim();
}

export function AmbientParticles(): string {
  const colors = ['bg-[rgba(255,45,107,0.4)]', 'bg-[rgba(160,32,240,0.35)]', 'bg-[rgba(45,107,255,0.3)]', 'bg-[rgba(255,215,0,0.25)]'];
  let particlesHtml = '';
  // Deterministic positions and properties for 15 particles
  const particles = [
    { size: 5, left: 10, dur: 18, del: 2, drift: -30, color: 0 },
    { size: 3, left: 25, dur: 14, del: 0, drift: 20, color: 1 },
    { size: 4, left: 40, dur: 20, del: 5, drift: -10, color: 2 },
    { size: 6, left: 60, dur: 15, del: 3, drift: 40, color: 3 },
    { size: 2, left: 80, dur: 22, del: 7, drift: -40, color: 0 },
    { size: 4, left: 15, dur: 16, del: 1, drift: 10, color: 1 },
    { size: 3, left: 35, dur: 19, del: 6, drift: 30, color: 2 },
    { size: 5, left: 50, dur: 13, del: 4, drift: -20, color: 3 },
    { size: 6, left: 70, dur: 21, del: 8, drift: 50, color: 0 },
    { size: 2, left: 90, dur: 17, del: 2, drift: -50, color: 1 },
    { size: 4, left: 5, dur: 15, del: 5, drift: 15, color: 2 },
    { size: 3, left: 45, dur: 18, del: 3, drift: -25, color: 3 },
    { size: 5, left: 65, dur: 14, del: 7, drift: 35, color: 0 },
    { size: 4, left: 85, dur: 20, del: 1, drift: -15, color: 1 },
    { size: 6, left: 55, dur: 16, del: 4, drift: 45, color: 2 },
  ];

  for (const p of particles) {
    particlesHtml += `<div class="absolute rounded-full opacity-0 animate-[floatParticle_linear_infinite] ${colors[p.color]}" style="width: ${p.size}px; height: ${p.size}px; left: ${p.left}vw; animation-duration: ${p.dur}s; animation-delay: ${p.del}s; --drift: ${p.drift}px;"></div>`;
  }
  return `<div class="fixed inset-0 pointer-events-none z-0 overflow-hidden">${particlesHtml}</div>`;
}
